import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { countPendingTags } from './tagModeration';

/**
 * Sidebar triage counts (head-only, cheap) so operators see where the backlog is
 * without opening each tab: pending venue photos, pending reverse-search queue,
 * open user reports, and user-typed tags awaiting approval. Fails soft to 0.
 */
export type NavBadges = {
  pendingVenuePhotos: number;
  pendingQueue: number;
  openReports: number;
  /**
   * Reads 0 until the tag-moderation schema exists — `countPendingTags` swallows a
   * missing RPC on purpose, so the badge simply does not appear rather than the
   * sidebar erroring on a backend that is still being written.
   */
  pendingTags: number;
};

const ZERO: NavBadges = { pendingVenuePhotos: 0, pendingQueue: 0, openReports: 0, pendingTags: 0 };

export function useNavBadges(enabled: boolean): NavBadges {
  const [badges, setBadges] = useState<NavBadges>(ZERO);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setBadges(ZERO);
      return;
    }
    try {
      const [vp, q, r, t] = await Promise.all([
        supabase
          .from('venue_photos')
          .select('id', { count: 'exact', head: true })
          .eq('approved', false)
          .eq('rejected', false),
        supabase
          .from('venue_admin_review_queue')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('user_reports')
          .select('id', { count: 'exact', head: true })
          .or('status.eq.open,status.is.null'),
        countPendingTags(),
      ]);
      setBadges({
        pendingVenuePhotos: vp.error ? 0 : vp.count ?? 0,
        pendingQueue: q.error ? 0 : q.count ?? 0,
        openReports: r.error ? 0 : r.count ?? 0,
        pendingTags: t,
      });
    } catch {
      setBadges(ZERO);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return badges;
}
